import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import Box, { BoxProps } from '../Box/Box';
import Button from '../Button/Button';
import Icon from '../Icon/Icon';
import { screens } from '../shared/styles';
import useWidgetScrollLock from './hooks/useWidgetScrollLock';
import { panelCard, panelContainer, widgetTrigger } from './Widget.styles';

type IconVariantTypes = Parameters<typeof Icon>[0]['variant'];

type WidgetContextValue = {
  expanded: boolean;
  toggle: () => void;
  triggerRef: React.RefObject<HTMLButtonElement>;
  icon: IconVariantTypes;
  expandedIcon: IconVariantTypes;
  panelWidth: number | string;
};

export const WidgetContext = createContext<WidgetContextValue | null>(null);

const useWidgetContext = () => {
  const ctx = useContext(WidgetContext);
  if (!ctx)
    throw new Error('Widget subcomponents must be used within <Widget>');
  return ctx;
};

export type WidgetProps = {
  /**
   * ARIA label for accessibility
   */
  ariaLabel: string;
  /**
   * Icon to display when widget is collapsed
   */
  icon?: IconVariantTypes;
  /**
   * Icon to display when widget is expanded
   */
  expandedIcon?: IconVariantTypes;
  /**
   * Default expanded state (uncontrolled)
   */
  defaultExpanded?: boolean;
  /**
   * Expanded state (controlled)
   */
  expanded?: boolean;
  /**
   * Callback when expanded state changes
   */
  onExpandedChange?: (expanded: boolean) => void;
  /**
   * Width of the widget panel (defaults to 480)
   */
  panelWidth?: number | string;
  /**
   * Additional CSS class names
   */
  className?: string;
  /**
   * Widget content
   */
  children?: React.ReactNode;
  /**
   * Additional props for the container
   */
  containerProps?: BoxProps;
  /**
   * Whether to close the widget when clicking outside
   * @default true
   */
  closeOnClickOutside?: boolean;
};

export type WidgetTriggerProps = {
  /**
   * Additional CSS class names
   */
  className?: string;
};

export type WidgetPanelProps = {
  /**
   * Additional CSS class names
   */
  className?: string;
  /**
   * Content to display within the widget panel
   */
  children?: React.ReactNode;
};

export const WidgetTrigger: React.FC<WidgetTriggerProps> = () => {
  const { expanded, toggle, triggerRef, icon, expandedIcon } =
    useWidgetContext();
  const currentIcon = expanded ? expandedIcon : icon;
  return (
    <>
      <Button
        ref={triggerRef}
        aria-expanded={expanded}
        aria-haspopup="dialog"
        onClick={toggle}
        size="lg"
        icon={<Icon variant={currentIcon} size="xxlarge" />}
        css={widgetTrigger}
      />
    </>
  );
};

export const WidgetPanel: React.FC<
  WidgetPanelProps & { style?: React.CSSProperties }
> = ({ className, style, children }) => {
  const { expanded, panelWidth } = useWidgetContext();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!expanded) return;
    const vv = window.visualViewport;
    if (!vv) return;

    const onResize = () => {
      const el = panelRef.current;
      if (!el) return;

      const isMobile = window.matchMedia(
        `(max-width: calc(${screens.lg} - 1px))`
      ).matches;
      if (!isMobile) {
        el.style.removeProperty('--widget-panel-height');
        el.style.removeProperty('--widget-panel-top');
        return;
      }

      el.style.setProperty('--widget-panel-height', `${vv.height}px`);
      el.style.setProperty('--widget-panel-top', `${vv.offsetTop}px`);
    };

    vv.addEventListener('resize', onResize);
    vv.addEventListener('scroll', onResize);
    onResize();

    return () => {
      vv.removeEventListener('resize', onResize);
      vv.removeEventListener('scroll', onResize);
    };
  }, [expanded]);

  if (!expanded) return null;

  return (
    <div
      ref={panelRef}
      css={panelContainer}
      className={className}
      style={style}
    >
      <Box
        css={panelCard(panelWidth)}
        role="dialog"
        aria-modal={true}
        aria-hidden={false}
      >
        {children}
      </Box>
    </div>
  );
};

const WidgetRoot: React.FC<WidgetProps> = ({
  ariaLabel,
  icon = 'Plus' as IconVariantTypes,
  expandedIcon = 'Xmark' as IconVariantTypes,
  defaultExpanded = false,
  expanded,
  onExpandedChange,
  panelWidth,
  className,
  children,
  containerProps,
  closeOnClickOutside = true,
}) => {
  const [internalExpanded, setInternalExpanded] =
    useState<boolean>(defaultExpanded);
  const isControlled = typeof expanded === 'boolean';
  const isExpanded = isControlled ? (expanded as boolean) : internalExpanded;

  const triggerRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const setExpanded = useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalExpanded(next);
      onExpandedChange?.(next);
      if (!next) {
        // return focus to trigger after close
        setTimeout(() => triggerRef.current?.focus(), 0);
      }
    },
    [isControlled, onExpandedChange]
  );

  const toggle = useCallback(
    () => setExpanded(!isExpanded),
    [isExpanded, setExpanded]
  );

  // Close on outside click
  useEffect(() => {
    if (!isExpanded || !closeOnClickOutside) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!containerRef.current) return;
      if (!containerRef.current.contains(target)) {
        setExpanded(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [isExpanded, setExpanded, closeOnClickOutside]);

  // Close on Escape
  useEffect(() => {
    if (!isExpanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpanded(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isExpanded, setExpanded]);

  useWidgetScrollLock(isExpanded, containerRef);

  const value = useMemo<WidgetContextValue>(
    () => ({
      expanded: isExpanded,
      toggle,
      triggerRef,
      icon,
      expandedIcon,
      panelWidth: panelWidth ?? 480,
    }),
    [expandedIcon, icon, isExpanded, panelWidth, toggle]
  );

  return (
    <Box
      ref={containerRef}
      className={className}
      aria-label={ariaLabel}
      position="fixed"
      right="var(--spacing-6)"
      bottom="var(--spacing-6)"
      zIndex={100}
      {...containerProps}
    >
      <WidgetContext.Provider value={value}>
        {children}
        {/* If consumer didn't place a header in Panel, we don't force it here; header rendering occurs inside stories layout */}
      </WidgetContext.Provider>
    </Box>
  );
};

export const Widget = Object.assign(WidgetRoot, {
  Trigger: WidgetTrigger,
  Panel: WidgetPanel,
});

export default Widget;
